export type SupportCenterFormKind =
  | 'psicologico'
  | 'nutricional'
  | 'fitness'
  | 'juridico'
  | 'social'

export const SUPPORT_CENTER_FORM_KIND_LABELS: Record<SupportCenterFormKind, string> = {
  psicologico: 'Psicológico',
  nutricional: 'Nutricional',
  fitness: 'Fitness',
  juridico: 'Jurídico',
  social: 'Assistência Social',
}

export function isSupportCenterFormKind(value: unknown): value is SupportCenterFormKind {
  return (
    value === 'psicologico' ||
    value === 'nutricional' ||
    value === 'fitness' ||
    value === 'juridico' ||
    value === 'social'
  )
}

/**
 * Kind operacional da assistência.
 * Vale quando specific_form_key é um dos formulários implementados.
 */
export function resolveAssistanceFormKind(
  specificFormKey: unknown
): SupportCenterFormKind | null {
  return isSupportCenterFormKind(specificFormKey) ? specificFormKey : null
}

/** Formulário do agendamento: vem de specific_form_key da assistência. */
export function appointmentFormKind(
  appointment: {
    assistance?: {
      specific_form_key?: unknown
      specific_form?: { form_key?: unknown } | null
    } | null
  } | null | undefined
): SupportCenterFormKind | null {
  const assistance = appointment?.assistance
  if (!assistance) return null
  return resolveAssistanceFormKind(
    assistance.specific_form?.form_key ?? assistance.specific_form_key
  )
}

export const ASSISTANCE_FORM_KIND_MISSING =
  'A assistência deste agendamento não tem formulário específico. Cadastre em Parâmetros → Formulários e vincule na Assistência.'

export type SupportCenterSpecializationAssistanceRef = {
  id: string
  name: string
  code: string
}

export interface SupportCenterSpecialization {
  id: string
  name: string
  description: string | null
  assistances: SupportCenterSpecializationAssistanceRef[]
  created_at: string
  updated_at: string
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
}

export type SupportCenterSpecializationInput = {
  id?: string
  name: string
  description: string
  /** IDs das assistências vinculadas (N:N). */
  assistance_ids: string[]
}

export interface SupportCenterProfileOption {
  id: string
  name: string
  email?: string | null
}

/** Função do profissional na Central de Amparo (uma por pessoa). */
export type SupportCenterProfessionalRoleKind = 'triagem' | 'atendente_triagem'

export const SUPPORT_CENTER_PROFESSIONAL_ROLE_LABELS: Record<
  SupportCenterProfessionalRoleKind,
  string
> = {
  triagem: 'Triagem',
  atendente_triagem: 'Atendente/Triagem',
}

export function isSupportCenterProfessionalRoleKind(
  value: unknown
): value is SupportCenterProfessionalRoleKind {
  return value === 'triagem' || value === 'atendente_triagem'
}

export interface SupportCenterProfessionalRole {
  id: string
  user_id: string
  role: SupportCenterProfessionalRoleKind
  created_at: string
  updated_at: string
  created_by: string | null
  updated_by: string | null
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
  profile?: { id: string; name: string } | null
}

export type SupportCenterProfessionalRoleInput = {
  user_id: string
  role: SupportCenterProfessionalRoleKind
  actor_id: string
}

export interface SupportCenterSpecializationAssignment {
  id: string
  user_id: string
  specialization_id: string
  created_at: string
  created_by: string | null
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
  profile?: { id: string; name: string } | null
  specialization?: { id: string; name: string } | null
}

export interface SupportCenterUserAvailability {
  id: string
  user_id: string
  weekdays: number[]
  hours: number[]
  created_at: string
  updated_at: string
  created_by: string | null
  updated_by: string | null
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
}

export type SupportCenterUserAvailabilityInput = {
  user_id: string
  weekdays: number[]
  hours: number[]
  actor_id: string
}

export interface SupportCenterUserUnavailability {
  id: string
  user_id: string
  starts_on: string
  ends_on: string
  all_day: boolean
  hours: number[]
  reason: string | null
  created_at: string
  updated_at: string
  created_by: string | null
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
  profile?: { id: string; name: string } | null
}

export type SupportCenterUserUnavailabilityInput = {
  id?: string
  user_id: string
  starts_on: string
  ends_on: string
  all_day: boolean
  hours: number[]
  reason: string
  actor_id: string
}

/** Feriado/bloqueio global (todos os profissionais). */
export interface SupportCenterHoliday {
  id: string
  name: string
  holiday_on: string
  all_day: boolean
  hours: number[]
  reason: string | null
  created_at: string
  updated_at: string
  created_by: string | null
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
}

export type SupportCenterHolidayInput = {
  id?: string
  name: string
  holiday_on: string
  all_day: boolean
  hours: number[]
  reason: string
  actor_id: string
}
